// Framework-agnostic port of the QuizzyLoot "fireAdLogic" ad engine.
//
// Behavior preserved exactly from the original inline script:
//  - recursive retry loop on `notReady` / `noAdPreloaded` (fixes the
//    "must click twice" bug),
//  - 1.5s debounce on the public entry points,
//  - abort support,
//  - frequencyCapped + terminal-status handling.
//
// Difference from the original: instead of mutating the DOM directly, this
// module returns Promises (so React can `await` ads) and reports status/loading
// through handlers registered by AdContext.

import { RETRY_LIMIT, RETRY_DELAY_MS, DEBOUNCE_MS } from './adConfig.js';

// --- handlers wired in by AdContext (no-ops until then) ---
let handlers = {
  onStatus: () => {},
  onLoading: () => {},
};

export function setHandlers(next) {
  handlers = { ...handlers, ...next };
}

function setStatus(text, theme = 'blue') {
  handlers.onStatus(text, theme);
}

function setLoading(show, text) {
  handlers.onLoading(show, text);
}

// --- internal state ---
let retries = 0;
let lastClick = 0;
let aborted = false;

// True only when the Google Ad Placement API actually loaded.
function adApiAvailable() {
  return typeof window !== 'undefined' && typeof window.adBreak === 'function';
}

// Mirrors abortRequest(): kills the current retry loop + clears the loader.
export function abort() {
  aborted = true;
  retries = RETRY_LIMIT + 1;
  setLoading(false);
  setStatus('Cancelled', 'blue');
}

/**
 * Recursive ad requester — the heart of the original harness.
 * `type` is 'next' (interstitial) or 'reward' (rewarded).
 * Resolves with { viewed } for rewarded; resolves (no payload needed) otherwise.
 */
function fireAdLogic(type, resolve) {
  if (aborted) {
    resolve({ viewed: false });
    return;
  }

  if (retries > RETRY_LIMIT) {
    setLoading(false);
    setStatus('No ad available', 'blue');
    resolve({ viewed: false });
    return;
  }

  setLoading(true, 'Loading ad…');
  setStatus('Please wait', 'blue');

  const adConfig = {
    type,
    name: `burst_${type}_${Date.now()}_${retries}`,
    beforeAd: () => {
      setLoading(false);
      setStatus('Advertisement', 'blue');
    },
    afterAd: () => {
      setStatus('Thanks for watching', 'blue');
      setLoading(false);
    },
    adBreakDone: (info) => {
      // eslint-disable-next-line no-console
      console.log(`[Ad Placement API] status: ${info.breakStatus}`);

      if (
        info.breakStatus === 'notReady' ||
        info.breakStatus === 'noAdPreloaded'
      ) {
        retries++;
        setTimeout(() => fireAdLogic(type, resolve), RETRY_DELAY_MS);
        return;
      }

      // Any other status is terminal for this break.
      setLoading(false);
      if (info.breakStatus === 'frequencyCapped') {
        setStatus('No ad right now', 'blue');
      } else {
        setStatus('Done', 'blue');
      }
      // Interstitials ALWAYS settle here (on any terminal status, including
      // unknown ones) so callers can never hang. Rewarded resolves via
      // adViewed/adDismissed below to preserve the correct viewed=true result.
      if (type !== 'reward') resolve({ viewed: false });
    },
  };

  if (type === 'reward') {
    adConfig.beforeReward = (showFn) => {
      setLoading(false);
      setStatus('Your reward is ready', 'green');
      showFn();
    };
    adConfig.adDismissed = () => {
      setStatus('Reward skipped', 'blue');
      setLoading(false);
      resolve({ viewed: false });
    };
    adConfig.adViewed = () => {
      setStatus('Reward earned', 'green');
      setLoading(false);
      resolve({ viewed: true });
    };
  }

  window.adBreak(adConfig);
}

/**
 * Public entry. Returns a Promise that resolves with { viewed: boolean }.
 *  - interstitial: viewed is unused by callers; resolves when the ad settles.
 *  - rewarded: viewed=true only if the user watched to completion.
 */
function showAd(type) {
  return new Promise((resolve) => {
    const now = Date.now();
    if (now - lastClick < DEBOUNCE_MS) {
      resolve({ viewed: false }); // debounced — treat as a no-op
      return;
    }
    lastClick = now;

    // No-API guard: if adsbygoogle is blocked/unavailable, never stall the quiz.
    if (!adApiAvailable()) {
      setStatus('Ads Unavailable', 'blue');
      resolve({ viewed: false });
      return;
    }

    retries = 0;
    aborted = false;
    setLoading(true, 'Loading ad…');
    setStatus('Please wait', 'blue');
    fireAdLogic(type, resolve);
  });
}

export function showInterstitial() {
  return showAd('next').then(() => undefined);
}

export function showRewarded() {
  return showAd('reward');
}

// Initializes the Ad Placement API (mirrors the original window load handler).
export function initAds(onReady) {
  if (typeof window !== 'undefined' && typeof window.adConfig === 'function') {
    window.adConfig({
      preloadAdBreaks: 'auto',
      sound: 'on',
      onReady: () => {
        setStatus('System Online', 'blue');
        if (onReady) onReady();
      },
    });
  }
}
